"use client";

import { useEffect, useState } from "react";
import {
  DEFAULT_LANG,
  LANGUAGES,
  readGoogTrans,
  setGoogleTranslateLanguage,
  type LangCode,
} from "@/lib/googleTranslate";

type LanguageToggleProps = {
  /**
   * `compact` — the navbar pill: script-only labels (EN / हिं) so it fits
   * beside Login and Free Trial without crowding them.
   * `full` — the mobile drawer: full language names, full width, because
   * there is room and a drawer row shouldn't be a two-letter riddle.
   */
  variant?: "compact" | "full";
  className?: string;
};

/**
 * English / Hindi switcher, driving the hidden Google Website Translator.
 *
 * Google's own widget renders a dropdown plus a fixed banner across the top of
 * the page; both are suppressed in landing.css and this stands in for them, so
 * the control matches the rest of the navbar.
 *
 * A segmented control rather than a dropdown: with exactly two options a
 * dropdown costs a click to show what a segment already shows, and the active
 * language stays visible at a glance.
 *
 * `notranslate` on the wrapper is load-bearing. Without it Google translates
 * its own switcher, so "English" renders as "अंग्रेज़ी" once Hindi is active —
 * and the one control a lost reader needs becomes unreadable to them.
 */
export default function LanguageToggle({
  variant = "compact",
  className = "",
}: LanguageToggleProps) {
  const compact = variant === "compact";

  /* Always starts at the default so the server HTML and the first client
     render agree; the cookie is read in an effect straight after hydration.
     Reading it during render instead would mark Hindi active against markup
     the server built as English, and React would throw out the tree. */
  const [lang, setLangState] = useState<LangCode>(DEFAULT_LANG);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const active = readGoogTrans();
    if (active) setLangState(active);
  }, []);

  const choose = (next: LangCode) => {
    if (next === lang || pending) return;
    setLangState(next);
    // Both directions reload (see setGoogleTranslateLanguage), so the buttons
    // latch to stop a second click racing the navigation.
    setPending(true);
    setGoogleTranslateLanguage(next);
  };

  return (
    <div
      role="group"
      aria-label="Change language"
      translate="no"
      className={`notranslate inline-flex items-center rounded-full border border-[#D6E0FC] bg-[#EFF3FE] ${
        // h-[42px] matches the Login / Free Trial buttons beside it
        // (px-5 py-2.5 text-[14px] + border) so the navbar reads as one row
        // of equal controls instead of a short pill next to tall buttons.
        compact ? "h-[42px] p-1 gap-0.5" : "p-1 gap-1 w-full"
      } ${className}`}
    >
      {LANGUAGES.map((option) => {
        const isActive = option.code === lang;
        return (
          <button
            key={option.code}
            type="button"
            onClick={() => choose(option.code)}
            disabled={pending}
            // aria-pressed, not aria-current: this is a pair of toggle
            // buttons, not navigation — nothing here is a "page".
            aria-pressed={isActive}
            // The visible label is abbreviated in the compact variant, so the
            // accessible name has to carry the full language name instead.
            aria-label={compact ? option.label : undefined}
            title={option.label}
            // `lang-pill-label` keeps the Devanagari webfont out of this
            // element's font stack — see the note on the class in landing.css.
            className={`lang-pill-label rounded-full font-bold whitespace-nowrap cursor-pointer
                        transition-colors duration-200 disabled:cursor-wait
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3D5EE1] focus-visible:ring-offset-1
                        ${compact ? "h-full inline-flex items-center px-3 text-[13px]" : "flex-1 px-3 py-2 text-[14px]"}
                        ${
                          isActive
                            ? "bg-white text-[#3D5EE1] shadow-[0_1px_3px_rgba(61,94,225,0.18)]"
                            : "text-[#4a5568] hover:text-[#3D5EE1]"
                        }`}
          >
            {compact ? option.short : option.label}
          </button>
        );
      })}
    </div>
  );
}
